import api from './api';
import { AxiosResponse } from 'axios';

export interface Agent {
  id: number;
  name: string;
  code: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  reservation_count?: number;
}

export interface AgentListResponse {
  results: Agent[];
  count: number;
  next: string | null;
  previous: string | null;
}

export interface AgentQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  country?: string;
  state?: string;
  city?: string;
  name__icontains?: string;
  ordering?: string;
}

export interface Reservation {
  id: number;
  res_no: string;
  crs_no: string | null;
  guest_name: string;
  arrival_date: string | null;
  departure_date: string | null;
  property_name: string;
  room_type: string | null;
  reservation_status: string;
  booked_date: string | null;
  tariff: number | null;
  revenue: number | null;
  res_value: number | null;
  market_segment: string | null;
  guest_type: string | null;
}

export interface ReservationListResponse {
  results: Reservation[];
  count: number;
  next: string | null;
  previous: string | null;
}

export interface ReservationQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
}

const agentService = {
  // Get agents with pagination, search, and filters
  getAgents: async (params?: AgentQueryParams): Promise<{ results: Agent[], count: number }> => {
    try {
      // Format the parameters for Django REST Framework
      const queryParams = { ...params };
      
      // Handle search differently if the backend expects specific field searches
      if (queryParams.search) {
        // Some Django backends might expect name__icontains instead of search
        queryParams.name__icontains = queryParams.search;
        delete queryParams.search;
      }
      
      // Updated URL to match the Django backend URL routing
      const response: AxiosResponse = await api.get('/agents/', { params: queryParams });
      
      // Handle Django REST Framework paginated response
      if (response.data.results && typeof response.data.count === 'number') {
        // Standard DRF paginated response
        return {
          results: response.data.results,
          count: response.data.count
        };
      } else if (Array.isArray(response.data)) {
        // Non-paginated response (just an array of agents)
        return {
          results: response.data,
          count: response.data.length
        };
      } else {
        // Fallback if the response doesn't match expected format
        console.warn('Unexpected API response format:', response.data);
        return {
          results: [],
          count: 0
        };
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  },

  // Get a single agent by ID
  getAgent: async (id: number): Promise<Agent> => {
    try {
      const response: AxiosResponse = await api.get(`/agents/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching agent with ID ${id}:`, error);
      throw error;
    }
  },

  // Get reservations for a specific agent
  getAgentReservations: async (
    agentId: number, 
    params?: ReservationQueryParams
  ): Promise<ReservationListResponse> => {
    try {
      const response: AxiosResponse = await api.get(`/agents/${agentId}/reservations/`, {
        params
      });

      if (response.data.results && typeof response.data.count === 'number') {
        // Standard DRF paginated response
        return response.data;
      } else if (Array.isArray(response.data)) {
        // Non-paginated response
        return {
          results: response.data,
          count: response.data.length,
          next: null,
          previous: null
        };
      } else {
        // Fallback
        console.warn('Unexpected API response format:', response.data);
        return {
          results: [],
          count: 0,
          next: null,
          previous: null
        };
      }
    } catch (error) {
      console.error(`Error fetching reservations for agent ID ${agentId}:`, error);
      throw error;
    }
  },

  // Create a new agent
  createAgent: async (agentData: Omit<Agent, 'id'>): Promise<Agent> => {
    try {
      const response: AxiosResponse = await api.post('/agents/', agentData);
      return response.data;
    } catch (error) {
      console.error('Error creating agent:', error);
      throw error;
    }
  },

  // Update an existing agent
  updateAgent: async (id: number, agentData: Partial<Agent>): Promise<Agent> => {
    try {
      const response: AxiosResponse = await api.put(`/agents/${id}/`, agentData);
      return response.data;
    } catch (error) {
      console.error(`Error updating agent with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete an agent
  deleteAgent: async (id: number): Promise<void> => {
    try {
      await api.delete(`/agents/${id}/`);
    } catch (error) {
      console.error(`Error deleting agent with ID ${id}:`, error);
      throw error;
    }
  }
};

export default agentService; 